// Input → semantic intents. Keyboard + touch (tap = jump, swipe = lane/jump),
// mirroring v1's bindings in js/ui-manager.js:387-468.

export type Intent =
  'lane-left' | 'lane-right' | 'jump' | 'slide' | 'pause' | 'start' | 'time-cycle';

export type IntentHandler = (intent: Intent) => void;

const SWIPE_THRESHOLD = 30; // px, matches v1 swipe detection

export class InputController {
  private touchStartX = 0;
  private touchStartY = 0;
  private touchMoved = false;
  private detachFns: (() => void)[] = [];

  constructor(private readonly onIntent: IntentHandler) {}

  attach(target: HTMLElement): void {
    const keydown = (e: KeyboardEvent): void => {
      switch (e.code) {
        case 'ArrowLeft':
        case 'KeyA':
          this.onIntent('lane-left');
          break;
        case 'ArrowRight':
        case 'KeyD':
          this.onIntent('lane-right');
          break;
        case 'Space':
        case 'ArrowUp':
        case 'KeyW':
          e.preventDefault();
          this.onIntent('jump');
          break;
        case 'ArrowDown':
        case 'KeyS':
          e.preventDefault();
          this.onIntent('slide');
          break;
        case 'Escape':
        case 'KeyP':
          this.onIntent('pause');
          break;
        case 'Enter':
          this.onIntent('start');
          break;
        case 'KeyT': // v1 debug binding: cycle time of day
          this.onIntent('time-cycle');
          break;
      }
    };

    const touchstart = (e: TouchEvent): void => {
      const touch = e.touches[0];
      if (!touch) return;
      this.touchStartX = touch.clientX;
      this.touchStartY = touch.clientY;
      this.touchMoved = false;
    };

    const touchmove = (e: TouchEvent): void => {
      const touch = e.touches[0];
      if (!touch) return;
      if (
        Math.abs(touch.clientX - this.touchStartX) > SWIPE_THRESHOLD ||
        Math.abs(touch.clientY - this.touchStartY) > SWIPE_THRESHOLD
      ) {
        this.touchMoved = true;
      }
      e.preventDefault(); // Block scroll/pull-to-refresh during gameplay
    };

    const touchend = (e: TouchEvent): void => {
      const touch = e.changedTouches[0];
      if (!touch) return;
      const dx = touch.clientX - this.touchStartX;
      const dy = touch.clientY - this.touchStartY;

      if (!this.touchMoved) {
        this.onIntent('jump'); // Tap
        return;
      }
      if (Math.abs(dx) > Math.abs(dy)) {
        this.onIntent(dx > 0 ? 'lane-right' : 'lane-left');
      } else if (dy < 0) {
        this.onIntent('jump'); // Swipe up
      } else {
        this.onIntent('slide'); // Swipe down
      }
      e.preventDefault();
    };

    window.addEventListener('keydown', keydown);
    target.addEventListener('touchstart', touchstart, { passive: true });
    target.addEventListener('touchmove', touchmove, { passive: false });
    target.addEventListener('touchend', touchend, { passive: false });

    this.detachFns = [
      () => window.removeEventListener('keydown', keydown),
      () => target.removeEventListener('touchstart', touchstart),
      () => target.removeEventListener('touchmove', touchmove),
      () => target.removeEventListener('touchend', touchend),
    ];
  }

  detach(): void {
    for (const fn of this.detachFns) fn();
    this.detachFns = [];
  }
}
